import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Printer, ListChecks, RefreshCw, Boxes, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/* Pull List — ONLY orders explicitly sent here from Packing. Combines them
   into one pull sheet with real stock allocation:
       physical stock - allocated stock = available stock
   Custom orders are only allocated when "Run Against Tuber Audit" is pressed. */

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const addr = (a) => !a ? "" : typeof a === "string" ? a
  : [a.line1, a.line2, a.suburb, a.city, a.postcode, a.region].filter(Boolean).join(", ");

const STATUS_STYLE = {
  Available:      { bg: "#EAF3EC", fg: "#2F6B4F" },
  Allocated:      { bg: "#3E1F46", fg: "#FFFFFF" },
  Pulled:         { bg: "#2F6B4F", fg: "#FFFFFF" },
  "Low Stock":    { bg: "#FBF0DC", fg: "#8A5D12" },
  Short:          { bg: "#A33030", fg: "#FFFFFF" },
  "Not Found":    { bg: "#3C2A1E", fg: "#FFFFFF" },
  "Needs Review": { bg: "#C49247", fg: "#3C2A1E" },
};

const Pill = ({ status, testid }) => {
  const s = STATUS_STYLE[status] || { bg: "#F3EBDD", fg: "#6E1E3A" };
  return (
    <span data-testid={testid} className="inline-flex px-2 py-0.5 rounded-full text-[9px] tracking-[0.16em] uppercase font-bold whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.fg }}>{status}</span>
  );
};

export default function AdminPullList() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showProposal, setShowProposal] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [runReport, setRunReport] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/pull-list");
      setData(r.data);
    } catch { toast.error("Couldn't build the pull list."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allocate = async () => {
    setBusy("allocate");
    try {
      const r = await api.post("/admin/pull-list/allocate", {});
      setRunReport({ title: "Stock allocated from the Tuber Audit", ...r.data });
      toast.success(`${r.data.allocated_total || 0} tuber${r.data.allocated_total === 1 ? "" : "s"} allocated across ${r.data.orders} order${r.data.orders === 1 ? "" : "s"}${r.data.short_total ? ` · ${r.data.short_total} still short` : " · nothing short"}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't allocate stock."); }
    finally { setBusy(""); }
  };

  const togglePulled = async (order, row) => {
    setBusy(`${order.id}:${row.variety_key}`);
    try {
      await api.post("/admin/pull-list/pull", { order_id: order.id, variety_key: row.variety_key, pulled: !row.pulled });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't tick that off."); }
    finally { setBusy(""); }
  };

  const removeOrder = async (order) => {
    if (!window.confirm(`Remove ${order.order_number} from the Pull List? Its allocated stock goes back to available.`)) return;
    setBusy(order.id);
    try {
      await api.post("/admin/pull-list/remove", { order_ids: [order.id] });
      toast.success(`${order.order_number} removed, stock released.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that order."); }
    finally { setBusy(""); }
  };

  const varietyNames = useMemo(() => (data?.varieties || []).map((v) => v.name), [data]);

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building the pull list…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-pull-list">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Selected from packing</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1 text-[#3C2A1E]">Pull List</h1>
          <p className="text-sm italic text-[#7A6A55] mt-1 max-w-2xl">
            {data.order_count === 0
              ? "Nothing here yet. Open Packing, tick the orders you want and press “Send to Pull List”."
              : `${data.order_count} order${data.order_count === 1 ? "" : "s"} · ${data.total_required} tubers required · ${data.total_allocated} allocated · ${data.total_shortage} short.`}
          </p>
          {data.scope && <p className="text-[11px] text-[#9C8B73] mt-1 max-w-2xl" data-testid="pull-list-scope">{data.scope}</p>}
          {(data.unresolved_text || []).length > 0 && (
            <div className="mt-2 border border-[#A33030]/40 bg-[#FFF4F0] rounded p-2 text-xs text-[#A33030]" data-testid="pull-list-unresolved">
              <strong>{data.unresolved_text.length} free-text selection{data.unresolved_text.length === 1 ? "" : "s"} need structured repair</strong> (not counted as shortages):
              <ul className="mt-1 space-y-0.5">{data.unresolved_text.map((u, i) => <li key={i}>#{u.order_number} {u.customer_name}: “{u.text}” × {u.qty}</li>)}</ul>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={load} data-testid="pull-list-refresh"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[#E8DFCB] bg-white text-[#3C2A1E] text-[11px] font-bold uppercase tracking-[0.14em]">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {showProposal && (
            <button type="button" onClick={() => { if (window.confirm("Propose reservations for every Pull List order? This writes proposed allocations (not physical packs). Confirm each box as you pack it.")) allocate(); }} disabled={data.order_count === 0 || busy === "allocate"} data-testid="pull-list-allocate"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-[#3E1F46] text-[#3E1F46] text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
              {busy === "allocate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Boxes className="w-4 h-4" />} Propose allocations (review)
            </button>
          )}
          <button type="button" onClick={() => setShowProposal((v) => !v)} data-testid="pull-list-advanced"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[#7A6A55] text-[10px] uppercase tracking-[0.14em] underline">{showProposal ? "Hide advanced" : "Advanced"}</button>
          <button type="button" onClick={() => printPullList(data)} disabled={data.order_count === 0} data-testid="pull-list-print"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            <Printer className="w-4 h-4" /> Print pull list
          </button>
        </div>
      </header>

      <div className="bg-white border border-[#E8DFCB] rounded p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="pull-list-audit-banner">
        <div className="text-sm text-[#3C2A1E]">
          <strong>How it works:</strong> tick orders in Packing → Send to Pull List → confirm each box as you physically pack it. Bulk “Propose allocations” sits under Advanced as a reviewed proposal only. Each order's tubers are taken from the Tuber Audit; anything set aside in a growing folder or the sell list is never touched. Short lines show in red so you can pick a backup.
        </div>
        <div className="text-[11px] text-[#7A6A55] ml-auto text-right">
          <div>Audit: <strong data-testid="pull-list-audit-status">{data.audit?.status === "complete" ? "Complete" : "In Progress"}</strong></div>
          <div>Last allocation: {data.audit?.last_custom_allocation_at ? new Date(data.audit.last_custom_allocation_at).toLocaleString("en-NZ") : "never"}</div>
          <a href="/admin/tuber-audit" className="font-bold uppercase tracking-[0.14em] text-[#6E1E3A] hover:underline">Open Tuber Audit →</a>
        </div>
      </div>

      {runReport && <RunReport report={runReport} onClose={() => setRunReport(null)} />}

      {data.order_count === 0 ? (
        <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded" data-testid="pull-list-empty">
          The Pull List is empty. Nothing is added automatically — you choose the orders in Packing.
        </div>
      ) : (
        <>
          <section className="bg-white border border-[#E8DFCB] rounded overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E8DFCB] text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1.5">
              <ListChecks className="w-4 h-4" /> Varieties required — combined
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-[9px] tracking-[0.2em] uppercase text-[#9C8B73] bg-[#FBF6E9]">
                    <th className="text-left px-4 py-2">Variety</th>
                    <th className="px-3 py-2">Required</th>
                    <th className="px-3 py-2">Available</th>
                    <th className="px-3 py-2">Allocated</th>
                    <th className="px-3 py-2">Remaining</th>
                    <th className="px-3 py-2">Short</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0E8D8]">
                  {data.varieties.map((v) => (
                    <tr key={v.variety_key} data-testid={`pull-variety-${v.variety_key.replace(/[^a-z0-9]+/gi, "-")}`}>
                      <td className="px-4 py-2 font-serif text-[15px] text-[#3C2A1E]">
                        {v.name}
                        <div className="text-[10px] text-[#9C8B73]">
                          {v.orders.map((s, i) => <span key={i} className="mr-2">{s.qty}× #{s.order_number} ({s.customer})</span>)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center font-bold">{v.required}</td>
                      <td className="px-3 py-2 text-center">{v.qty_available}</td>
                      <td className="px-3 py-2 text-center">{v.allocated}</td>
                      <td className="px-3 py-2 text-center">{v.remaining_stock}</td>
                      <td className={`px-3 py-2 text-center font-bold ${v.shortage ? "text-[#A33030]" : "text-[#9C8B73]"}`}>{v.shortage || "–"}</td>
                      <td className="px-3 py-2 text-center"><Pill status={v.status} testid={`pull-variety-status-${v.variety_key.replace(/[^a-z0-9]+/gi, "-")}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Orders — allocate &amp; tick off</div>
            {data.orders.map((o) => (
              <article key={o.id} className="bg-white border border-[#E8DFCB] rounded" data-testid={`pull-order-${o.id}`}>
                <div className="p-4 border-b border-[#F0E8D8] flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <strong className="font-serif text-[16px] text-[#3C2A1E]">{o.customer_name || o.email || "Unknown"}</strong>
                  <span className="text-xs text-[#9C8B73]">#{o.order_number}</span>
                  <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#F3EBDD] text-[#6E1E3A] font-semibold">{o.box_type}</span>
                  {o.is_custom && !o.box_type.toLowerCase().includes("custom") && <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#C49247] text-[#3C2A1E] font-semibold" data-testid={`pull-order-custom-${o.id}`}>Custom</span>}
                  {o.pack_folder && <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#3E1F46] text-white font-semibold">{o.pack_folder}</span>}
                  <span className={`text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded font-semibold ${o.payment_status === "paid" ? "bg-emerald-700 text-white" : "bg-[#F4D06F] text-[#6E1E3A]"}`}>{o.payment_status}</span>
                  <Pill status={o.status} testid={`pull-order-status-${o.id}`} />
                  <span className="ml-auto flex items-center gap-3">
                    <span className="text-xs text-[#9C8B73]">NZ${Number(o.total || 0).toFixed(2)}</span>
                    <button type="button" onClick={() => removeOrder(o)} disabled={busy === o.id} data-testid={`pull-remove-${o.id}`}
                      title="Remove from the Pull List and release its stock"
                      className="p-1.5 border border-[#E8DFCB] rounded text-[#A33030] hover:border-[#A33030]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                </div>
                {(addr(o.address) || o.notes || o.admin_notes || o.hq_picks_note) && (
                  <div className="px-4 py-2 text-xs text-[#7A6A55] space-y-0.5 border-b border-[#F0E8D8]">
                    {addr(o.address) && <div>{addr(o.address)}</div>}
                    {o.hq_picks_note && <div className="italic text-[#6E1E3A]">HQ note: {o.hq_picks_note}</div>}
                    {o.notes && <div className="italic">Order notes: {o.notes}</div>}
                    {o.admin_notes && <div className="italic text-[#A05A20]">Admin notes: {o.admin_notes}</div>}
                    {o.backups.length > 0 && <div>Backup varieties: {o.backups.join(", ")}</div>}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[760px]">
                    <thead>
                      <tr className="text-[9px] tracking-[0.2em] uppercase text-[#9C8B73] bg-[#FBF6E9]">
                        <th className="text-left px-4 py-2">Variety</th>
                        <th className="px-3 py-2">Req</th>
                        <th className="px-3 py-2">Avail</th>
                        <th className="px-3 py-2">Alloc</th>
                        <th className="px-3 py-2">Remain</th>
                        <th className="px-3 py-2">Short</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Pulled</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0E8D8]">
                      {o.rows.length === 0 && (
                        <tr><td colSpan={9} className="px-4 py-3 text-xs italic text-[#A33030]">No varieties on this order yet — open the order and add its items.</td></tr>
                      )}
                      {o.rows.map((row) => (
                        <tr key={row.variety_key} data-testid={`pull-row-${o.id}-${row.variety_key.replace(/[^a-z0-9]+/gi, "-")}`}>
                          <td className="px-4 py-2">
                            <span className="font-serif text-[15px] text-[#3C2A1E]">{row.name}</span>
                            <span className="ml-2 text-[10px] text-[#9C8B73] uppercase tracking-[0.15em]">{row.sources.join(" · ")}</span>
                            {row.substitute && (
                              <span className="ml-2 text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#3E1F46] text-white font-bold" data-testid={`pull-substitute-${o.id}-${row.variety_key.replace(/[^a-z0-9]+/gi, "-")}`}>Substitute</span>
                            )}
                            {row.resolution && (
                              <div className="text-[10px] text-[#8A5D12] italic">
                                Resolved: {row.resolution.action}{row.resolution.replacement_name ? ` → ${row.resolution.replacement_name}` : ""}{row.resolution.note ? ` (${row.resolution.note})` : ""}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center font-bold">{row.required}</td>
                          <td className="px-3 py-2 text-center">{row.qty_available}</td>
                          <td className="px-3 py-2 text-center">{row.qty_allocated}</td>
                          <td className="px-3 py-2 text-center">{row.remaining_stock}</td>
                          <td className={`px-3 py-2 text-center font-bold ${row.shortage ? "text-[#A33030]" : "text-[#9C8B73]"}`}>{row.shortage || "–"}</td>
                          <td className="px-3 py-2 text-center"><Pill status={row.status} testid={`pull-row-status-${o.id}-${row.variety_key.replace(/[^a-z0-9]+/gi, "-")}`} /></td>
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={row.pulled} disabled={row.qty_allocated === 0 || busy === `${o.id}:${row.variety_key}`}
                              onChange={() => togglePulled(o, row)} data-testid={`pull-tick-${o.id}-${row.variety_key.replace(/[^a-z0-9]+/gi, "-")}`}
                              title={row.qty_allocated === 0 ? "Allocate stock first" : "Tick when physically pulled"}
                              className="w-4 h-4 accent-[#2F6B4F]" />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {(row.shortage > 0 || row.status === "Needs Review") && (
                              <button type="button" onClick={() => setResolving({ order: o, row })} data-testid={`pull-resolve-${o.id}-${row.variety_key.replace(/[^a-z0-9]+/gi, "-")}`}
                                className="inline-flex items-center gap-1 px-2 py-1 border border-[#A33030] text-[#A33030] rounded text-[9px] font-bold uppercase tracking-[0.14em]">
                                <ShieldAlert className="w-3 h-3" /> Resolve
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            ))}
          </section>
        </>
      )}

      {resolving && (
        <ResolveModal
          order={resolving.order}
          row={resolving.row}
          varieties={varietyNames}
          onClose={() => setResolving(null)}
          onDone={async () => { setResolving(null); await load(); }}
        />
      )}
    </div>
  );
}

function RunReport({ report, onClose }) {
  return (
    <div className="bg-white border-l-4 border-[#C49247] border border-[#E8DFCB] rounded p-4" data-testid="pull-run-report">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#8A5D12]">{report.title}</div>
        <button type="button" onClick={onClose} aria-label="Close" data-testid="pull-run-report-close"><X className="w-4 h-4 text-[#9C8B73]" /></button>
      </div>
      {report.audit_complete === false && (
        <div className="mt-2 text-xs font-semibold text-[#A33030]">Heads up — the Tuber Audit is still marked In Progress. Mark it complete when the count is finished.</div>
      )}
      <div className="mt-2 grid gap-3 sm:grid-cols-2 text-xs text-[#3C2A1E]">
        <div>
          <div className="font-bold uppercase tracking-[0.15em] text-[10px] text-[#2F6B4F] mb-1">Allocated ({report.allocated?.length || 0})</div>
          {(report.allocated || []).length === 0 ? <div className="italic text-[#9C8B73]">Nothing new to allocate.</div> : (
            <ul className="space-y-0.5">{report.allocated.map((a, i) => <li key={i}>{a.qty}× {a.name} → #{a.order_number}</li>)}</ul>
          )}
        </div>
        <div>
          <div className="font-bold uppercase tracking-[0.15em] text-[10px] text-[#A33030] mb-1">Shortages ({report.shortages?.length || 0})</div>
          {(report.shortages || []).length === 0 ? <div className="italic text-[#9C8B73]">Everything covered.</div> : (
            <ul className="space-y-0.5">{report.shortages.map((s, i) => (
              <li key={i}>#{s.order_number} · {s.name} — short {s.shortage} of {s.required} (audit {s.audit_stock}, master {s.master_stock}){s.known ? "" : " · not found"}</li>
            ))}</ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ResolveModal({ order, row, varieties, onClose, onDone }) {
  const [action, setAction] = useState(row.shortage > 0 ? "replacement" : "unresolved");
  const [replacement, setReplacement] = useState("");
  const [qty, setQty] = useState(row.shortage || 1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/pull-list/resolve", {
        order_id: order.id, variety_key: row.variety_key, action,
        replacement_name: replacement, qty: Number(qty) || 0, note,
      });
      toast.success(r.data?.allocated ? `Allocated ${r.data.allocated}× ${replacement}.` : "Decision recorded.");
      if (r.data?.warning) toast.warning(r.data.warning);
      await onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that decision."); }
    finally { setBusy(false); }
  };

  const backups = order.backups || [];

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 p-4 grid place-items-center" role="dialog" aria-modal="true" data-testid="pull-resolve-modal">
      <div className="w-full max-w-lg bg-[#FBF6E9] border shadow-2xl max-h-[92vh] overflow-y-auto" style={{ borderColor: "#C49247" }}>
        <header className="p-5 border-b flex items-start justify-between" style={{ borderColor: "#E8DFCB" }}>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">#{order.order_number} · {order.customer_name}</div>
            <h2 className="font-serif text-3xl mt-1 text-[#3C2A1E]">{row.name}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="pull-resolve-close"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-5 space-y-4 text-sm text-[#3C2A1E]">
          <dl className="grid grid-cols-2 gap-y-1 gap-x-4 text-xs bg-white border border-[#E8DFCB] p-3 rounded">
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Requested</dt><dd className="font-bold">{row.name}</dd>
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Quantity required</dt><dd className="font-bold">{row.required}</dd>
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Audit stock</dt><dd>{row.audit_stock}</dd>
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Master stock</dt><dd>{row.master_stock}</dd>
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Already allocated</dt><dd>{row.qty_allocated}</dd>
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Remaining stock</dt><dd>{row.remaining_stock}</dd>
            <dt className="text-[#9C8B73] uppercase tracking-[0.15em]">Shortage</dt><dd className="font-bold text-[#A33030]">{row.shortage}</dd>
          </dl>

          <div className="space-y-2">
            {[
              { v: "replacement", l: "Choose a replacement variety" },
              { v: "backup", l: `Use a customer backup${backups.length ? "" : " (none on file)"}` },
              { v: "reduce", l: "Reduce the quantity" },
              { v: "unresolved", l: "Leave unresolved — needs review" },
              { v: "contact", l: "Contact the customer" },
            ].map((opt) => (
              <label key={opt.v} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="resolve-action" checked={action === opt.v} onChange={() => setAction(opt.v)}
                  data-testid={`pull-resolve-action-${opt.v}`} />
                {opt.l}
              </label>
            ))}
          </div>

          {action === "replacement" && (
            <label className="block text-xs">
              <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Replacement variety</span>
              <input list="pull-resolve-varieties" value={replacement} onChange={(e) => setReplacement(e.target.value)}
                data-testid="pull-resolve-replacement" className="w-full h-10 px-3 border bg-white" style={{ borderColor: "#E8DFCB" }} />
              <datalist id="pull-resolve-varieties">{varieties.map((n) => <option key={n} value={n} />)}</datalist>
            </label>
          )}
          {action === "backup" && (
            backups.length === 0
              ? <div className="text-xs italic text-[#A33030]">No backup varieties on this order — pick a replacement or contact the customer.</div>
              : <div className="flex flex-wrap gap-2">
                  {backups.map((b) => (
                    <button key={b} type="button" onClick={() => setReplacement(b)} data-testid={`pull-resolve-backup-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs border ${replacement === b ? "bg-[#3E1F46] text-white border-[#3E1F46]" : "bg-white border-[#E8DFCB]"}`}>{b}</button>
                  ))}
                </div>
          )}
          {(action === "replacement" || action === "backup" || action === "reduce") && (
            <label className="block text-xs max-w-[160px]">
              <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">{action === "reduce" ? "New quantity" : "Quantity"}</span>
              <input type="number" min="0" max={action === "reduce" ? row.required : Math.max(row.shortage, 1)} value={qty}
                onChange={(e) => setQty(e.target.value)} data-testid="pull-resolve-qty"
                className="w-full h-10 px-3 border bg-white" style={{ borderColor: "#E8DFCB" }} />
            </label>
          )}
          <label className="block text-xs">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Note (kept on the order)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="pull-resolve-note"
              className="w-full p-3 border bg-white text-sm" style={{ borderColor: "#E8DFCB" }} />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="h-10 px-4 border text-sm" style={{ borderColor: "#E8DFCB" }} data-testid="pull-resolve-cancel">Cancel</button>
            <button type="button" onClick={submit} disabled={busy} data-testid="pull-resolve-submit"
              className="h-10 px-5 bg-[#6E1E3A] text-white text-sm font-semibold disabled:opacity-40">
              {busy ? "Saving…" : "Save decision"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function printPullList(data) {
  const varietyRows = data.varieties.map((v) =>
    `<tr><td class="qty">${v.required}</td><td><strong>${esc(v.name)}</strong><div class="src">${v.orders.map((s) => `${s.qty}× #${esc(s.order_number)} (${esc(s.customer)})`).join(" &nbsp;·&nbsp; ")}</div></td><td class="qty">${v.qty_available}</td><td class="qty">${v.allocated}</td><td class="qty">${v.shortage || "–"}</td><td>${esc(v.status)}</td></tr>`).join("");
  const orderBlocks = data.orders.map((o) => `
    <div class="slip">
      <div class="head"><strong>☐ ${esc(o.customer_name || o.email || "Unknown")}</strong> <span>#${esc(o.order_number)}</span>
        <span class="tag">${esc(o.box_type)}</span>
        ${o.is_custom ? `<span class="tag custom">custom</span>` : ""}
        <span class="tag ${o.payment_status === "paid" ? "ok" : "warn"}">${esc(o.payment_status)}</span>
        ${o.pack_folder ? `<span class="tag folder">${esc(o.pack_folder)}</span>` : ""}
        <span class="tag">${esc(o.status)}</span>
      </div>
      ${addr(o.address) ? `<div class="addr">${esc(addr(o.address))}</div>` : ""}
      <table class="rows">
        <tr><th>Variety</th><th>Req</th><th>Alloc</th><th>Short</th><th>Status</th><th>Pulled</th></tr>
        ${o.rows.map((r) => `<tr><td>${esc(r.name)}</td><td class="qty">${r.required}</td><td class="qty">${r.qty_allocated}</td><td class="qty">${r.shortage || "–"}</td><td>${esc(r.status)}</td><td class="qty">${r.pulled ? "✓" : "☐"}</td></tr>`).join("")}
      </table>
      ${o.backups.length ? `<div class="note">Backups: ${esc(o.backups.join(", "))}</div>` : ""}
      ${o.hq_picks_note ? `<div class="note">HQ note: ${esc(o.hq_picks_note)}</div>` : ""}
      ${o.notes ? `<div class="note">Order notes: ${esc(o.notes)}</div>` : ""}
      ${o.admin_notes ? `<div class="note admin">Admin notes: ${esc(o.admin_notes)}</div>` : ""}
    </div>`).join("");
  const html = `<!doctype html><html><head><title>Pull list</title><style>
    body{font-family:Georgia,'Times New Roman',serif;color:#3C2A1E;margin:32px;}
    h1{font-size:22px;margin:0;} h2{font-size:14px;letter-spacing:.2em;text-transform:uppercase;margin:26px 0 8px;border-bottom:2px solid #C9A268;padding-bottom:4px;}
    .meta{font-size:12px;opacity:.7;margin-top:4px;}
    table{border-collapse:collapse;width:100%;font-size:13px;}
    th{text-align:left;font-size:10px;letter-spacing:.15em;text-transform:uppercase;border-bottom:2px solid #3C2A1E;padding:4px 8px;}
    td{border:1px solid #E5D9C5;padding:6px 10px;vertical-align:top;} .qty{text-align:center;font-weight:bold;width:56px;}
    .src{font-size:10px;opacity:.65;margin-top:2px;}
    .slip{border:1px solid #E5D9C5;border-radius:6px;padding:8px 12px;margin-bottom:10px;page-break-inside:avoid;font-size:13px;}
    .head{display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;margin-bottom:4px;}
    .addr{font-size:11px;opacity:.75;margin-bottom:4px;}
    .rows td,.rows th{font-size:12px;padding:4px 8px;}
    .note{font-size:11px;font-style:italic;color:#6E1E3A;margin-top:3px;} .note.admin{color:#A05A20;}
    .tag{font-size:9px;letter-spacing:.15em;text-transform:uppercase;border-radius:8px;padding:1px 8px;background:#F3EBDD;}
    .tag.ok{background:#2F6B4F;color:#fff;} .tag.warn{background:#F4D06F;color:#6E1E3A;} .tag.folder{background:#3E1F46;color:#fff;} .tag.custom{background:#C49247;}
    @media print{.noprint{display:none;}}
  </style></head><body>
    <div class="noprint" style="text-align:right"><button onclick="window.print()" style="padding:8px 18px;cursor:pointer;">Print</button></div>
    <h1>Heritage Dahlias — Pull List</h1>
    <div class="meta">${data.order_count} orders · ${data.total_required} required · ${data.total_allocated} allocated · ${data.total_shortage} short · audit ${esc(data.audit?.status || "")} · ${new Date().toLocaleString("en-NZ")}</div>
    <h2>Varieties required</h2>
    <table><tr><th>Req</th><th>Variety</th><th>Avail</th><th>Alloc</th><th>Short</th><th>Status</th></tr>${varietyRows}</table>
    <h2>Orders</h2>
    ${orderBlocks}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
